import { useEffect } from 'react';
import { Box, Button, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Building2, Plus, Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOrganizationStore } from '@/store/superAdmin/organizationStore';
import { PageHeader } from '@/components/navigation/PageHeader';
import { tokens } from '@/theme/tokens';
import { Loader } from '@/components/feedback/Loader';
import { ErrorState } from '@/components/feedback/ErrorState';

export function OrganizationListPage() {
  const navigate = useNavigate();
  const { organizations, loading, error, fetchOrganizations } = useOrganizationStore();

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  if (loading && organizations.length === 0) return <Loader message="Loading organizations..." />;
  if (error) return <ErrorState message={error} onRetry={fetchOrganizations} />;

  const columns = [
    { 
      field: 'organization_name', 
      headerName: 'Organization Name', 
      flex: 1.5,
      minWidth: 280,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, overflow: 'hidden' }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: tokens.radius.sm,
              backgroundColor: tokens.colors.primary[50],
              color: tokens.colors.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Building2 size={16} />
          </Box>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ color: tokens.colors.text.primary, display: 'block' }}>
              {params.value || 'Unnamed Organization'}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: tokens.colors.text.secondary, display: 'block' }}>
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { field: 'phone', headerName: 'Phone', flex: 0.8, minWidth: 110 },
    { field: 'city', headerName: 'City', flex: 0.8, minWidth: 100 },
    { field: 'state', headerName: 'State', flex: 0.8, minWidth: 100 },
    { field: 'country', headerName: 'Country', flex: 0.7, minWidth: 90 },
    { field: 'address', headerName: 'Address', flex: 1, minWidth: 130 },
    { field: 'postal_code', headerName: 'Postal Code', flex: 0.7, minWidth: 90 },

    { 
      field: 'users', 
      headerName: 'Users', 
      flex: 0.5, 
      minWidth: 70,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? params.value.length : 0}
        </Typography>
      )
    },

    { 
      field: 'status', 
      headerName: 'Status', 
      flex: 0.6, 
      minWidth: 80,
      renderCell: (params) => (
        <Chip
          label={params.value === 'ACTIVE' || params.value === 'active' ? 'Active' : 'Inactive'}
          size="small"
          sx={{
            backgroundColor: params.value === 'ACTIVE' || params.value === 'active' ? tokens.colors.success[50] : tokens.colors.error[50],
            color: params.value === 'ACTIVE' || params.value === 'active' ? tokens.colors.success.main : tokens.colors.error.main,
            fontWeight: 600,
            borderRadius: tokens.radius.sm,
          }}
        />
      ),
    },
    { 
      field: 'created_at', 
      headerName: 'Created Date', 
      flex: 0.7, 
      minWidth: 100,
      valueFormatter: (value) => {
        if (!value) return 'N/A';
        return new Date(value).toLocaleDateString();
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      minWidth: 80,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: '100%' }}>
          <Tooltip title="Edit Organization">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/super-admin/organizations/edit/${params.row.id}`);
              }}
              sx={{ color: tokens.colors.primary.main }}
            >
              <Edit size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title="Organizations"
        subtitle="Manage all onboarded organizations across the platform."
        actions={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => navigate('/super-admin/organizations/add')}
            sx={{
              borderRadius: tokens.radius.sm,
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: tokens.shadows.sm,
              }
            }}
          >
            Create Organization
          </Button>
        }
      />

      <Box sx={{ width: '100%', backgroundColor: tokens.colors.card, borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, overflow: 'hidden' }}>
        <DataGrid
          rows={organizations}
          columns={columns}
          autoHeight
          getRowHeight={() => 'auto'}
          disableRowSelectionOnClick
          onRowClick={(params) => navigate(`/super-admin/organizations/${params.row.id}`)}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderColor: tokens.colors.border,
              display: 'flex',
              alignItems: 'center',
              py: 1.5,
              outline: 'none !important',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: tokens.colors.grey[50],
              borderBottom: `1px solid ${tokens.colors.border}`,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: tokens.colors.grey[50],
              cursor: 'pointer',
            },
          }}
        />
      </Box>
    </Box>
  );
}
